import { useEffect, useState } from 'react';
import { getCurrentUser } from '../utils/api';
import { favoriteTutor, unfavoriteTutor, fetchFavoriteTutorIds } from '../services/tutorFavoritesService';

const StarIcon = ({ filled }) => (
  <svg
    className="w-5 h-5"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

/**
 * Favorite/unfavorite toggle for a tutor. `variant="badge"` renders the
 * compact icon-only badge used on tutor cards; `variant="button"` renders
 * the icon+label button used on the tutor's profile page, matching the
 * existing favorite toggle in the Dashboard's My Tutors list.
 */
const TutorFavoriteButton = ({
  tutorProfileId,
  variant = 'badge',
  initialFavorited,
  onChange,
  className = '',
}) => {
  const currentUser = getCurrentUser();
  const isStudent = currentUser?.role === 'student';

  const [favorited, setFavorited] = useState(Boolean(initialFavorited));
  const [loading, setLoading] = useState(initialFavorited === undefined);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!isStudent || initialFavorited !== undefined) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchFavoriteTutorIds()
      .then((ids) => {
        if (!cancelled) {
          setFavorited(Array.isArray(ids) && ids.includes(tutorProfileId));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorProfileId, isStudent]);

  if (!isStudent) {
    return null;
  }

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggling || loading) return;

    const next = !favorited;
    setFavorited(next);
    setToggling(true);
    try {
      if (next) {
        await favoriteTutor(tutorProfileId);
      } else {
        await unfavoriteTutor(tutorProfileId);
      }
      onChange?.(next);
    } catch (error) {
      setFavorited(!next);
    } finally {
      setToggling(false);
    }
  };

  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading || toggling}
        title={favorited ? 'Remove from favorites' : 'Add to favorites'}
        className={`w-full flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] rounded-lg font-medium border transition-colors touch-manipulation ${
          favorited
            ? 'bg-yellow-50 border-yellow-400 text-yellow-600 hover:bg-yellow-100'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        } ${loading || toggling ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
      >
        <StarIcon filled={favorited} />
        {favorited ? 'Favorited' : 'Favorite'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={loading || toggling}
      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${
        favorited
          ? 'bg-brand-gold text-white'
          : 'bg-gray-100 text-gray-400 hover:bg-brand-gold/20 hover:text-brand-gold'
      } ${loading || toggling ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
    >
      <StarIcon filled={favorited} />
    </button>
  );
};

export default TutorFavoriteButton;
